import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardActions from "@mui/material/CardActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Link } from "react-router-dom";
import { useCreatePath, useStore } from "react-admin";
import { formatEventCardDateRange } from "@/utils/date-format";
import { navigateToBaseRoute } from "@/utils/route-utility";
import type { CurrentEvent, EventsListRecord } from "@/containers/events/types";

const styles = {
  grid: {
    marginTop: "0px",
  },
  tile: {
    maxHeight: "350px",
  },
  card: {},
};

const LoadingGridList = () => (
  <Grid container spacing={2} alignItems="center" sx={styles.grid}>
    {[...Array(8).keys()].map((i) => (
      <Grid
        key={i}
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <Skeleton variant="rectangular" width="100%" height={310} />
      </Grid>
    ))}
  </Grid>
);

const editEvent = (id: string | number) => {
  const { href } = window.location;
  const url = new URL(href);
  window.location.href = `${url.pathname}#/events/${id}`;
};

type CardGridListProps = { data: EventsListRecord };

const CardGridList = ({ data }: CardGridListProps) => {
  const [, setCurrentEvent] = useStore<CurrentEvent | null>("currentEvent", null);
  const createPath = useCreatePath();
  const showPath = createPath({ resource: "events", type: "show", id: data.id });

  const handleClick = () => {
    setCurrentEvent(data as CurrentEvent);
    localStorage.setItem(
      "currEvent",
      JSON.stringify({
        id: data.id,
        name: data.name,
        type: data.type,
        hijriYear: data.hijriYear,
        slug: data.slug,
        zabihatAmount: data.zabihatAmount,
        chairAmount: data.chairAmount,
        mehmaanAmount: data.mehmaanAmount,
        relayAmount: data.relayAmount,
        pirsaAmount: data.pirsaAmount,
        relayEventId: data.relayEventId,
        relayEvent: data.relayEvent,
        venues: data.venues,
        sequences: data.sequences,
      })
    );
    navigateToBaseRoute("events", String(data.id));
  };

  return (
    <Grid
      sx={styles.tile}
      key={data.id}
      size={{
        xs: 12,
        sm: 6,
        lg: 3,
      }}
    >
      <Card sx={styles.card}>
        <CardActionArea onClick={handleClick}>
          <Box
            sx={{
              display: "flex",
              aspectRatio: "2 / 1",
              justifyContent: "center",
              bgcolor: "background.paper",
              p: 2,
            }}
          >
            <img
              src={
                data.image || `https://placehold.jp/30/1a237e/ffffff/330x100.png?text=${data.name}`
              }
              style={{ maxWidth: "100%", maxHeight: "100%", height: "auto", alignSelf: "center" }}
              alt={data.name}
            />
          </Box>
        </CardActionArea>
        <CardContent sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}>
          <Typography component="div">{data.name}</Typography>
          <Typography>{formatEventCardDateRange(data.fromDate, data.toDate)}</Typography>
        </CardContent>
        <CardActions>
          <Button size="small" color="primary" onClick={handleClick}>
            <Typography color="primary">View</Typography>
          </Button>
          <Button size="small" color="primary" component={Link} to={showPath}>
            <Typography color="primary">RSVP</Typography>
          </Button>
          <Button size="small" color="primary" onClick={() => editEvent(data.id)}>
            <Typography color="primary">Edit</Typography>
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
};

const LoadedGridList = ({ events }: { events: EventsListRecord[] }) => (
  <Box sx={{ marginBottom: { xs: 6, sm: 12 }, minWidth: 0 }}>
    {events.length > 0 && (
      <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch" sx={styles.grid}>
        {events.map((d) => (
          <CardGridList data={d} key={d.id} />
        ))}
      </Grid>
    )}
  </Box>
);

type EventsDashboardGridListProps = { isLoading?: boolean; events?: EventsListRecord[] };

export const EventsDashboardGridList = ({ isLoading, ...props }: EventsDashboardGridListProps) =>
  isLoading ? <LoadingGridList /> : <LoadedGridList events={props.events ?? []} />;
